import { FlagsProvider, useFlag } from '@zuruflex/api-client/react';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { lazy, StrictMode, Suspense, type ReactNode } from 'react';
import { createRoot } from 'react-dom/client';
import { ErrorBoundary, MaintenanceBanner, ThemeProvider } from '@zuruflex/ui';
import { RouterProvider } from 'react-router-dom';
import './i18n.js';
import './index.css';
import { api } from './lib/api.js';
import { AuthProvider, useAuth } from './lib/auth.js';
import { router } from './router.js';

const queryClient = new QueryClient({
  defaultOptions: {
    queries: { staleTime: 30_000, refetchOnWindowFocus: false, retry: 1 },
  },
});

/**
 * The seeded logins, floating over every screen while developing.
 *
 * Loaded this way on purpose: `import.meta.env.DEV` folds to `false` in a
 * production build, taking the dynamic import — and the demo passwords
 * inside it — out of the bundle entirely.
 */
const DevLogins = import.meta.env.DEV
  ? lazy(() => import('./components/DevLogins.js').then((m) => ({ default: m.DevLogins })))
  : null;

/**
 * The flags, for everything under the session.
 *
 * Inside `AuthProvider` because who is signed in is part of the question —
 * and of the query key, so a different staff member signing in refetches.
 * No anonymous id: nothing here is reachable signed out but the sign-in
 * page. Staff see the maintenance banner too, which is the point — it is
 * how whoever raised it confirms what everybody else is looking at.
 */
function AdminFlags({ children }: { children: ReactNode }) {
  const { user } = useAuth();
  return (
    <FlagsProvider client={api} surface="admin" principalKey={user?.id ?? null}>
      <AdminMaintenanceBanner />
      {children}
    </FlagsProvider>
  );
}

function AdminMaintenanceBanner() {
  return <MaintenanceBanner value={useFlag('app.maintenanceBanner')} />;
}

const root = document.getElementById('root');
if (!root) throw new Error('#root is missing from index.html');

createRoot(root).render(
  <StrictMode>
    {/* The router carries its own `errorElement`; this catches the
        layer above it — the query client, the theme, the session —
        where a throw would otherwise leave a blank document. */}
    <ErrorBoundary>
      <QueryClientProvider client={queryClient}>
        <ThemeProvider themeColor={{ light: '#ffffff', dark: '#121213' }}>
          <AuthProvider>
            {/* Inside the session, because who is signed in is part of
                what a flag is worth; outside the router, so the
                maintenance banner is above every page it has. */}
            <AdminFlags>
              <RouterProvider router={router} />
            </AdminFlags>
            {DevLogins ? (
              <Suspense fallback={null}>
                <DevLogins />
              </Suspense>
            ) : null}
          </AuthProvider>
        </ThemeProvider>
      </QueryClientProvider>
    </ErrorBoundary>
  </StrictMode>,
);
